import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { CONTAINER, EYEBROW, IMG, NAMESPACE, NewTabLink, PauseChip, Rich, SECTION, SECTION_HEAD_CENTER, SECTION_LEAD, SECTION_TITLE } from '../components/ui';

const ROW_A: [string, string, number, number][] = [
  ['game-valorant-256', 'Valorant', 256, 341],
  ['game-cs2-256', 'Counter-Strike 2', 256, 341],
  ['game-fortnite-256', 'Fortnite', 256, 341],
  ['game-lol-256', 'League of Legends', 256, 341],
  ['game-apex-256', 'Apex Legends', 256, 341],
  ['game-dota2-256', 'Dota 2', 256, 341],
  ['game-pubg-256', 'PUBG', 256, 341],
  ['game-tarkov-256', 'Escape from Tarkov', 256, 341],
  ['game-overwatch2-256', 'Overwatch 2', 256, 341],
];
const ROW_B: [string, string, number, number][] = [
  ['game-wow-256', 'World of Warcraft', 256, 341],
  ['game-r6-248', 'Rainbow Six Siege', 248, 330],
  ['game-ffxiv-256', 'Final Fantasy XIV', 256, 341],
  ['game-ragnarok-256', 'Ragnarok Online', 256, 341],
  ['game-albion-250', 'Albion Online', 250, 332],
  ['game-lineage2-256', 'Lineage 2', 256, 341],
  ['game-tibia-248', 'Tibia', 248, 330],
  ['game-starcraft-256', 'StarCraft', 256, 341],
  ['game-warface-248', 'Warface', 248, 330],
];

const TRACK = 'marquee__track m-0 flex flex-none list-none gap-[var(--mq-g)] py-0 pe-[var(--mq-g)] ps-0 [&>li]:h-[var(--mq-h)] [&>li]:w-[var(--mq-w)] [&>li]:flex-none [&>li]:overflow-hidden [&>li]:rounded-[.75rem] [&>li]:border [&>li]:border-white/10 [&>li]:[background:linear-gradient(150deg,rgb(147_255_24/.07),rgb(255_255_255/.02)_46%),var(--surface-2)] [&>li]:shadow-[0_2px_6px_rgb(0_0_0/.45),0_1px_2px_rgb(0_0_0/.3)] [&_img]:block [&_img]:size-full [&_img]:bg-[#1C1E24] [&_img]:object-cover [&_img]:object-[center_62%] [&_img]:text-transparent';
const TRACK_B = '[&>li]:opacity-[0.78] [&>li]:[filter:saturate(.88)_brightness(.93)]';

function Track({ covers, loaded, hidden, className }: { covers: [string, string, number, number][]; loaded: boolean; hidden: boolean; className: string }) {
  return (
    <ul className={className} role={hidden ? 'presentation' : undefined} aria-hidden={hidden || undefined}>
      {covers.map(([file, name, width, height]) => (
        <li key={file}>
          <picture>
            <source type='image/avif' srcSet={loaded ? `${IMG}/${file}.avif` : undefined} />
            <img src={loaded ? `${IMG}/${file}.webp` : undefined} alt={hidden ? '' : name} width={width} height={height} loading={loaded ? 'eager' : 'lazy'} decoding='async' />
          </picture>
        </li>
      ))}
    </ul>
  );
}

export function Games() {
  const { t } = useTranslation(NAMESPACE);
  const wrapRef = useRef<HTMLDivElement>(null);
  const seedRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [copies, setCopies] = useState(2);
  const [coversLoaded, setCoversLoaded] = useState(false);

  // enough copies of each lane to tile the widest viewport it can meet, the second half mirroring the
  // first so the −50% loop is seamless at any width
  useEffect(() => {
    const fill = () => {
      const seed = seedRef.current?.querySelector('.marquee__track');
      const width = seed?.getBoundingClientRect().width ?? 0;
      if (!width) return;
      setCopies(Math.max(1, Math.ceil((window.innerWidth + 240) / width)) * 2);
    };
    fill();
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fill, 260);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearTimeout(timer);
    };
  }, []);

  // covers are fetched on approach (700px), never on first paint; once approached every one is loaded
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setCoversLoaded(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCoversLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '700px 0px' }
    );
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  // is-playing is the ONLY thing that starts the lanes, so the button delivers exactly what it announces
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPaused(motion.matches);
    const onChange = (event: MediaQueryListEvent) => setPaused(event.matches);
    motion.addEventListener?.('change', onChange);
    return () => motion.removeEventListener?.('change', onChange);
  }, []);

  const lanes = [
    { key: 'a', covers: ROW_A, className: TRACK },
    { key: 'b', covers: ROW_B, className: `${TRACK} ${TRACK_B}` },
  ];

  return (
    <section className={SECTION} id='jogos' aria-labelledby='games-title'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__grid' />
      </div>
      <div className={CONTAINER}>
        <div className={SECTION_HEAD_CENTER} data-reveal>
          <p className={EYEBROW}>{t('games.eyebrow')}</p>
          <h2 className={SECTION_TITLE} id='games-title'>
            <Rich i18nKey='games.title' />
          </h2>
          <p className={`${SECTION_LEAD} mx-auto`}>{t('games.lead')}</p>
        </div>
        <div className='flex flex-wrap items-center justify-between gap-3 pb-4 [@media(min-width:63.5em)_and_(max-width:65em)]:flex-nowrap' data-reveal>
          <p className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0 text-[.75rem] font-semibold uppercase tracking-[.06em] text-[#9AA3B5] [&>i]:size-1.5 [&>i]:flex-none [&>i]:rounded-full [&>i]:bg-[#93FF18] [&>i]:shadow-[0_0_8px_rgb(147_255_24/.8)] forced-colors:[&>i]:bg-[CanvasText] forced-colors:[&>i]:shadow-none [@media(min-width:63.5em)_and_(max-width:65em)]:flex-[1_1_auto]'>
            <i aria-hidden='true' />
            <span className='whitespace-nowrap [@media(width<19.5rem)]:whitespace-normal'>{t('games.catalog')}</span>
            <span className="text-[#9AA3B5] before:me-[.5em] before:content-['·'] [@media(width<720px)]:mt-[.2rem] [@media(width<720px)]:flex-[1_0_100%] [@media(width<720px)]:before:content-none">{t('games.auto')}</span>
          </p>
          <PauseChip paused={paused} onToggle={() => setPaused((previous) => !previous)} pauseAria={t('games.pause_aria')} resumeAria={t('games.resume_aria')} pauseLabel={t('common.pause')} resumeLabel={t('common.resume')} controls='games-marquee' />
        </div>
      </div>

      <div ref={wrapRef} className={`marquee ${paused ? 'is-paused' : 'is-playing'}`} id='games-marquee' data-marquee>
        {lanes.map((lane) => (
          <div key={lane.key} ref={lane.key === 'a' ? seedRef : undefined} className={`marquee__row marquee__row--${lane.key}`}>
            {Array.from({ length: copies }, (_, copy) => (
              <Track key={copy} covers={lane.covers} loaded={coversLoaded} hidden={copy > 0} className={lane.className} />
            ))}
          </div>
        ))}
      </div>

      <div className={CONTAINER}>
        <p className='mt-[clamp(1.25rem,.9rem+1.4vw,2rem)] text-center text-[.875rem] text-[#9AA3B5] [&_a]:font-semibold [&_a]:text-[#93FF18] [&_a]:underline-offset-[.25em] [@media(pointer:coarse)]:[&_a]:inline-flex [@media(pointer:coarse)]:[&_a]:min-h-11 [@media(pointer:coarse)]:[&_a]:items-center [@media(pointer:coarse)]:[&_a]:py-2' data-reveal>
          <Rich
            i18nKey='games.missing'
            components={{
              link: <NewTabLink href='/games' />,
            }}
          />
        </p>
      </div>
    </section>
  );
}
